import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { Action, createReducer, on } from '@ngrx/store';

import * as LiveFiltersActions from '../actions/live-filters.actions';

import INextPage = Microsoft.VideoIndexer.Contracts.INextPage;

export interface ListFilter<T = unknown[]> {
  id: string;
  data?: T;
  loaded?: boolean;
  isLoading?: boolean;
  error?: boolean;
  nextPage?: INextPage;
  appliedFilterValues?: Record<string, string>[];
}

// We use this initialFilterState but we omit the appliedFilterValues and id properties cause they need to be present in the state
const initialFilterState: Required<Omit<ListFilter, 'appliedFilterValues' | 'id'>> = {
  loaded: false,
  error: false,
  isLoading: false,
  data: [],
  nextPage: undefined
};

export interface IState extends EntityState<ListFilter> {}

export function selectFilterId(filter: ListFilter): string {
  return filter.id;
}

export const adapter: EntityAdapter<ListFilter> = createEntityAdapter<ListFilter>({
  selectId: selectFilterId
});

// NOTE: no need to initialize the state with data since we dynamically building the filters based on actions
export const initialState: IState = adapter.getInitialState({});

const liveFiltersReducer = createReducer(
  initialState,
  on(LiveFiltersActions.applyFilter, (state, { filterType, filterValues }) => {
    return adapter.upsertOne(
      {
        id: filterType,
        appliedFilterValues: filterValues
      },
      state
    );
  }),
  on(LiveFiltersActions.loadFilter, (state, { filterType }) => {
    return adapter.upsertOne(
      {
        id: filterType,
        ...initialFilterState
      },
      state
    );
  }),
  on(LiveFiltersActions.loadFilterStarted, (state, { filterType }) => {
    return adapter.updateOne(
      {
        id: filterType,
        changes: {
          isLoading: true
        }
      },
      state
    );
  }),
  on(LiveFiltersActions.loadFilterSucceeded, (state, { filterType, filter, nextPage }) => {
    return adapter.updateOne(
      {
        id: filterType,
        changes: {
          loaded: true,
          error: false,
          isLoading: false,
          data: [...(state.entities[filterType]?.data || []), ...filter],
          nextPage
        }
      },
      state
    );
  }),
  on(LiveFiltersActions.loadFilterFailed, (state, { filterType }) => {
    return adapter.updateOne(
      {
        id: filterType,
        changes: {
          loaded: false,
          error: true,
          isLoading: false
        }
      },
      state
    );
  }),
  on(LiveFiltersActions.clearFilter, (state, { filterType }) => {
    return adapter.updateOne(
      {
        id: filterType,
        changes: initialFilterState
      },
      state
    );
  }),

  on(LiveFiltersActions.removeFilter, (state, { filterType }) => {
    return adapter.removeOne(filterType, state);
  }),
  on(LiveFiltersActions.removeFilters, (state, { filterNamespace }) => {
    if (filterNamespace === undefined) {
      return adapter.removeAll(state);
    }
    const filterTypes = Object.keys(state.entities).filter(filterType => {
      return filterType.startsWith(String(filterNamespace));
    });
    return adapter.removeMany(filterTypes, state);
  })
);

export function reducer(state: IState | undefined, action: Action) {
  return liveFiltersReducer(state, action);
}
